import { useNavigate } from 'react-router-dom'

import { DashboardNoticeList } from '@/components/dashboard/DashboardNoticeList'
import { DashboardPriorityItems } from '@/components/dashboard/DashboardPriorityItems'
import { DashboardQuickLinks } from '@/components/dashboard/DashboardQuickLinks'
import { DashboardRevenueSummary } from '@/components/dashboard/DashboardRevenueSummary'
import { DashboardSectionLink } from '@/components/dashboard/DashboardSectionLink'
import { DashboardSettlementSummary } from '@/components/dashboard/DashboardSettlementSummary'
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { OperationVerificationSummaryCard } from '@/components/venue/OperationVerificationSummaryCard'
import { useReservationRefundSummary } from '@/hooks/useReservationRefundSummary'
import { useVenueDashboard } from '@/hooks/useVenueDashboard'
import { useVenueRevenueDashboard } from '@/hooks/useVenueRevenueDashboard'
import type { VenueDashboardView } from '@/lib/venue/dashboardTypes'
import {
  toDashboardCancellationStat,
  toDashboardCourtsViewModel,
  toDashboardNoticeItems,
  toDashboardPriorityItemViewModels,
  toDashboardRevenueStat,
  toDashboardSettlementViewModel,
} from '@/lib/venue/dashboardViewModel'
import type { ReservationRefundSummaryView } from '@/lib/venue/reservationRefundTypes'
import type { VenueRevenueDashboardView } from '@/lib/venue/revenueTypes'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 내 구장 대시보드 — Figma `관리자 대시보드`(13989-46325).
 *
 * 🚨 이 화면은 조립만 한다. 상태 판정·표현 매핑은 `dashboardViewModel.ts`, 서식은 `format.ts` 가 소유한다(설계 §2).
 */
export function VenueDashboardPage() {
  const { venueId } = useVenueContext()

  const dashboardQuery = useVenueDashboard(venueId)
  const refundSummaryQuery = useReservationRefundSummary(venueId)
  const revenueDashboardQuery = useVenueRevenueDashboard(venueId)

  return (
    <>
      <PageHeader title="대시보드" description="구장 운영 현황을 한눈에 확인합니다." />

      {dashboardQuery.isLoading ? <LoadingState rows={6} label="대시보드를 불러오는 중" /> : null}

      {dashboardQuery.isError ? (
        <ErrorState
          error={dashboardQuery.error}
          onRetry={() => {
            void dashboardQuery.refetch()
          }}
        />
      ) : null}

      {dashboardQuery.data ? (
        <DashboardContent
          data={dashboardQuery.data}
          venueId={venueId}
          refundSummary={refundSummaryQuery.data}
          isRefundSummaryLoading={refundSummaryQuery.isLoading}
          revenueDashboard={revenueDashboardQuery.data}
          isRevenueDashboardLoading={revenueDashboardQuery.isLoading}
        />
      ) : null}
    </>
  )
}

function DashboardContent({
  data,
  venueId,
  refundSummary,
  isRefundSummaryLoading,
  revenueDashboard,
  isRevenueDashboardLoading,
}: {
  data: VenueDashboardView
  venueId: string
  // 🚨 취소·환불 요약은 2차 보강 데이터라, 실패해도 이 컴포넌트는 그대로 렌더된다(설계 §2-1(f)).
  refundSummary: ReservationRefundSummaryView | undefined
  isRefundSummaryLoading: boolean
  // 🚨 #793 — "이번 달 매출 요약"(예약 축, `/revenues/dashboard`)도 같은 원칙의 2차 보강 데이터다.
  // 실패해도 대시보드 나머지 섹션은 그대로 렌더되고, 이 카드만 자리표시자를 보여준다.
  revenueDashboard: VenueRevenueDashboardView | undefined
  isRevenueDashboardLoading: boolean
}) {
  const navigate = useNavigate()
  const courts = toDashboardCourtsViewModel(data.courts)
  const cancellationStat = toDashboardCancellationStat(refundSummary)
  const revenueStat = toDashboardRevenueStat(data.settlement)
  const settlement = toDashboardSettlementViewModel(data.settlement)
  const noticeItems = toDashboardNoticeItems(data.noticeDispatches)
  const priorityItems = toDashboardPriorityItemViewModels(data.priorityItems, venueId)

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* 🔴 예약 도메인 원천 부재(대시보드 EP `reservations` 는 항상 null) — "준비 중" 고정(설계 §2-1(d)) */}
        <DashboardStatCard label="오늘 예약" value="준비 중" subText="예약 도메인 연동 후 표시됩니다" />
        <DashboardStatCard label="예약 가능 코트" value={courts.valueText} subText={courts.subText} />
        <DashboardStatCard
          label="취소 요청"
          value={cancellationStat.valueText}
          isLoading={isRefundSummaryLoading}
          badge={cancellationStat.badgeText ? { text: cancellationStat.badgeText, tone: 'danger' } : null}
        />
        <DashboardStatCard label="이번 달 매출" value={revenueStat.valueText} subText={revenueStat.subText} />
      </div>

      {/*
        🚨 #793 — "이번 달 매출 요약"(예약 축, `/revenues/dashboard`). 위 4-타일의 "이번 달 매출"
        (회차 축, `/dashboard`)과 별개 EP·별개 카드다 — 기존 타일·"정산 요약" 카드는 그대로 두고
        추가만 했다(설계 §2 자율 결정 4). 두 EP의 숫자를 이 화면 코드가 합산·비교하지 않는다.

        🚨 `sales/revenue`는 `navItems.ts`에 등록되지 않은 하위 라우트라(홈 카드로만 진입하는 관례,
        navItems.ts:65 주석 참고) `DashboardSectionLink`(navItems 상태로 활성/비활성을 가르는 컴포넌트)
        를 쓰면 "준비 중" 배지로 잘못 비활성화된다 — `VenueSalesHomePage`의 하위 메뉴 카드와 같은
        방식으로 직접 이동한다.
      */}
      <Card>
        <CardHeader
          title="이번 달 매출 요약"
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                navigate(`/venues/${venueId}/sales/revenue`)
              }}
            >
              매출 현황 보기
            </Button>
          }
        />
        <CardBody>
          {isRevenueDashboardLoading ? (
            <LoadingState rows={2} label="이번 달 매출 요약을 불러오는 중" />
          ) : revenueDashboard ? (
            <DashboardRevenueSummary data={revenueDashboard} />
          ) : (
            <EmptyState title="이번 달 매출 요약을 불러오지 못했습니다" />
          )}
        </CardBody>
      </Card>

      {/* 🚨 #782 — `priorityItems` 가 비어 있으면 섹션 자체를 렌더하지 않는다(설계 §2 자율 결정 2). */}
      {priorityItems.length > 0 ? (
        <Card>
          <CardHeader title="우선 처리 항목" />
          <CardBody>
            <DashboardPriorityItems items={priorityItems} />
          </CardBody>
        </Card>
      ) : null}

      {/* 🚨 #778 — EP-A(`badge` 5상태) 기반 공용 요약 카드. 대시보드·구장정보 화면이 같은 컴포넌트를
          쓴다(설계 §2 자율 결정 3 — 대시보드 EP 내장 3필드로는 UNDER_REVIEW/REJECTED 를 구분 못 한다). */}
      <OperationVerificationSummaryCard venueId={venueId} />

      <Card>
        <CardHeader
          title="오늘 예약"
          action={<DashboardSectionLink venueId={venueId} path="bookings/list" label="전체 보기" />}
        />
        <CardBody>
          <EmptyState
            title="예약 데이터는 준비 중입니다"
            description="예약 도메인 연동이 완료되면 이 영역에 표시됩니다."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="운영 공지 발송 현황"
          action={<DashboardSectionLink venueId={venueId} path="notices" label="공지·홍보 관리" />}
        />
        <CardBody>
          <DashboardNoticeList items={noticeItems} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="정산 요약"
          action={<DashboardSectionLink venueId={venueId} path="sales" label="정산 상세 보기" />}
        />
        <CardBody>
          <DashboardSettlementSummary settlement={settlement} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="빠른 이동" />
        <CardBody>
          <DashboardQuickLinks venueId={venueId} />
        </CardBody>
      </Card>
    </div>
  )
}
